/**
 * Bottom Sheet AutoComplete Component
 * Renders an AutoComplete in a modal with a trigger input
 */

import { ContentModal, useModalRef, type ContentModalRef } from "./ContentModal";
import { LocationTextInput } from "./LocationTextInput";
import { Text } from "../../mads";
import { useI18n } from "../../shared/I18n";

import React, { useCallback, type ReactNode } from "react";
import { type ViewStyle, type DimensionValue, StyleSheet } from "react-native";

interface BottomSheetAutoCompleteProps {
  /** ID for the modal */
  id: string;
  /** Content to render in the modal (AutoComplete component) */
  children: ReactNode;
  /** Modal height */
  height?: DimensionValue;
  /** Value to display in the input */
  value: string;
  /** Handler for value changes */
  onChangeText?: (text: string) => void;
  /** Whether input is clearable */
  clearable?: boolean;
  /** Test ID */
  testID?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Error message */
  errorMessage?: string | null;
  /** Input style */
  inputStyle?: ViewStyle;
  /** Modal ref to use */
  modalRef?: React.RefObject<ContentModalRef | null>;
  /** Callback when modal opens */
  onModalOpen?: () => void;
  /** Accessibility label for the input field */
  ariaLabel?: string;
  /** Visible label text to show above the input */
  label?: ReactNode;
  /** Helper text rendered between the label and the trigger input */
  helperText?: string;
}

export function BottomSheetAutoComplete({
  id,
  children,
  height = "85%",
  value,
  onChangeText,
  clearable,
  testID,
  placeholder,
  errorMessage,
  inputStyle,
  modalRef: externalModalRef,
  onModalOpen,
  ariaLabel,
  label,
  helperText,
}: BottomSheetAutoCompleteProps) {
  const i18n = useI18n();
  const internalModalRef = useModalRef();
  const modalRef = externalModalRef || internalModalRef;

  const openModal = useCallback(() => {
    onModalOpen?.();
    modalRef.current?.present();
  }, [modalRef, onModalOpen]);

  return (
    <>
      <ContentModal id={id} height={height} ref={modalRef}>
        {children}
      </ContentModal>
      {label && (
        <Text level={3} variant={"contrast"} sx={styles.locationText} nativeID="locationLabel">
          {typeof label === "string" ? label : i18n.gettext("Location")}
        </Text>
      )}
      {helperText && (
        <Text level={3} sx={styles.helperText}>
          {helperText}
        </Text>
      )}
      <LocationTextInput
        clearable={clearable}
        editable={false}
        value={value}
        onChangeText={onChangeText}
        testID={testID}
        onPress={openModal}
        placeholder={placeholder}
        errorMessage={errorMessage}
        inputStyle={inputStyle}
        ariaLabel={ariaLabel}
        accessibilityLabelledBy={label ? ["locationLabel"] : undefined}
      />
    </>
  );
}

const styles = StyleSheet.create({
  locationText: {
    alignSelf: "auto",
    fontWeight: "bold",
  },
  helperText: {
    opacity: 0.85,
    marginBottom: 4,
  },
});
